// src/pages/funnels/FunnelStats.jsx
// Stats tab: per-step conversion bars + daily views/opt-ins time series.
// Data from /api/funnels?resource=stats (counters + funnel_daily_stats RPC).
import { useState, useEffect } from 'react';
import { authFetch } from '../../lib/authFetch';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui';
import { formatNumber, formatDate } from '../../lib/utils';
import { Loader2 } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

export default function FunnelStats({ funnelId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await authFetch(`/api/funnels?resource=stats&funnelId=${funnelId}&days=${days}`);
        const data = await res.json();
        if (!cancelled && data.success) setStats(data);
      } catch (e) {
        console.error('Stats load error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [funnelId, days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!stats) return <p className="text-sm text-muted-foreground py-8 text-center">Failed to load stats.</p>;

  // Collapse per-step daily rows into one series per day.
  const byDay = new Map();
  for (const row of stats.daily || []) {
    const day = row.day;
    const entry = byDay.get(day) || { day, views: 0, optins: 0 };
    entry.views += Number(row.views) || 0;
    entry.optins += Number(row.optins) || 0;
    byDay.set(day, entry);
  }
  const series = [...byDay.values()]
    .sort((a, b) => (a.day < b.day ? -1 : 1))
    .map(d => ({ ...d, label: formatDate(d.day, { month: 'short', day: 'numeric', year: undefined }) }));

  const maxViews = Math.max(1, ...stats.steps.map(s => s.views));

  return (
    <div className="space-y-4">
      {/* Overview tiles */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total Views', value: formatNumber(stats.funnel.total_views) },
          { label: 'Leads', value: formatNumber(stats.funnel.total_leads) },
          { label: 'Conversion Rate', value: `${stats.funnel.cvr}%` },
        ].map(tile => (
          <Card key={tile.label}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{tile.label}</p>
              <p className="text-2xl font-bold mt-1">{tile.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Step-by-step funnel */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Step-by-step</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {stats.steps.map(step => (
            <div key={step.id}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium">
                  {step.step_order}. {step.name}
                </span>
                <span className="text-muted-foreground text-xs">
                  {formatNumber(step.views)} views · {formatNumber(step.optins)} opt-ins
                  {step.views > 0 && ` · ${step.cvr}%`}
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${Math.max(2, (step.views / maxViews) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Time series */}
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Last {days} days</CardTitle>
          <div className="flex gap-1">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  days === d ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {series.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No traffic in this period yet. Publish the funnel and share its URL to start collecting data.
            </p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="views" name="Views" stroke="#6366f1" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="optins" name="Opt-ins" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
